import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, Circle, ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { aiIntelligenceService, OnboardingContext } from '../../services/aiIntelligenceService';
import { WelcomeStep } from './steps/WelcomeStep';
import { IdentityStep } from './steps/IdentityStep';
import { FormationStep } from './steps/FormationStep';
import { InPossessionStep } from './steps/InPossessionStep';
import { OutOfPossessionStep } from './steps/OutOfPossessionStep';
import { TransitionsStep } from './steps/TransitionsStep';
import { PrinciplesStep } from './steps/PrinciplesStep';
import { SummaryStep } from './steps/SummaryStep';

export interface WizardStepConfig {
  id: number;
  label: string;
  shortLabel: string;
}

const WIZARD_STEPS: WizardStepConfig[] = [
  { id: 0, label: 'Welcome', shortLabel: 'Welcome' },
  { id: 1, label: 'Identity', shortLabel: 'Identity' },
  { id: 2, label: 'Formation', shortLabel: 'Formation' },
  { id: 3, label: 'In Possession', shortLabel: 'In Possession' },
  { id: 4, label: 'Out of Possession', shortLabel: 'Out of Possession' },
  { id: 5, label: 'Transitions', shortLabel: 'Transitions' },
  { id: 6, label: 'Principles', shortLabel: 'Principles' },
  { id: 7, label: 'Summary', shortLabel: 'Summary' },
];

interface OnboardingWizardProps {
  teamId: string;
  onComplete: () => void;
}

export function OnboardingWizard({ teamId, onComplete }: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Wizard data state
  const [wizardData, setWizardData] = useState<Partial<OnboardingContext>>({
    identity: { coachingStyle: '', styleOfPlay: '', description: '' },
    formation: { preferred: [], teamShape: '', notes: '' },
    inPossession: { philosophy: '', pressingTriggers: [], buildUpStyle: '' },
    outOfPossession: { philosophy: '', defensiveStructure: '', pressingApproach: '' },
    transitions: { attackToDefence: '', defenceToAttack: '' },
    principles: { developmentPriorities: [], coachObjectives: [], seasonGoals: [] },
    trainingLogistics: { daysPerWeek: 2, sessionDurationMinutes: 60, ageGroup: '', competitiveLevel: '' },
  });

  // Load existing wizard progress on mount
  useEffect(() => {
    let cancelled = false;
    const loadProgress = async () => {
      try {
        const status = await aiIntelligenceService.getWizardStatus(teamId);
        if (!cancelled) {
          if (status.completedSteps && status.completedSteps.length > 0) {
            setCompletedSteps(status.completedSteps);
            // Resume from the next incomplete step
            const maxCompleted = Math.max(...status.completedSteps);
            setCurrentStep(Math.min(maxCompleted + 1, WIZARD_STEPS.length - 1));
          }
        }
      } catch {
        // If no progress exists, start from beginning
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    loadProgress();
    return () => { cancelled = true; };
  }, [teamId]);

  const updateStepData = useCallback((stepKey: keyof OnboardingContext, data: any) => {
    setWizardData((prev) => ({ ...prev, [stepKey]: data }));
  }, []);

  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 0: // Welcome - always valid
        return true;
      case 1: // Identity
        return !!(wizardData.identity?.coachingStyle && wizardData.identity?.styleOfPlay);
      case 2: // Formation
        return !!(wizardData.formation?.preferred && wizardData.formation.preferred.length > 0 && wizardData.formation?.teamShape);
      case 3: // In Possession
        return !!(wizardData.inPossession?.philosophy && wizardData.inPossession?.buildUpStyle);
      case 4: // Out of Possession
        return !!(wizardData.outOfPossession?.philosophy && wizardData.outOfPossession?.defensiveStructure);
      case 5: // Transitions
        return !!(wizardData.transitions?.attackToDefence && wizardData.transitions?.defenceToAttack);
      case 6: // Principles
        return !!(
          wizardData.principles?.developmentPriorities &&
          wizardData.principles.developmentPriorities.length > 0 &&
          wizardData.trainingLogistics?.ageGroup &&
          wizardData.trainingLogistics?.competitiveLevel &&
          wizardData.trainingLogistics?.daysPerWeek &&
          wizardData.trainingLogistics?.sessionDurationMinutes
        );
      case 7: // Summary - always valid (review step)
        return true;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    if (!validateCurrentStep()) return;

    // Save partial step (steps 1-6 map to data steps 1-7)
    if (currentStep > 0 && currentStep < 7) {
      try {
        const stepDataMap: Record<number, Record<string, unknown>> = {
          1: { identity: wizardData.identity },
          2: { formation: wizardData.formation },
          3: { inPossession: wizardData.inPossession },
          4: { outOfPossession: wizardData.outOfPossession },
          5: { transitions: wizardData.transitions },
          6: { principles: wizardData.principles, trainingLogistics: wizardData.trainingLogistics },
        };
        await aiIntelligenceService.savePartialStep(teamId, currentStep, stepDataMap[currentStep]);
      } catch {
        // Partial save failure is non-blocking
      }
    }

    // Mark step as completed
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps((prev) => [...prev, currentStep]);
    }

    setCurrentStep((prev) => Math.min(prev + 1, WIZARD_STEPS.length - 1));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const context: OnboardingContext = {
        identity: wizardData.identity!,
        formation: wizardData.formation!,
        inPossession: wizardData.inPossession!,
        outOfPossession: wizardData.outOfPossession!,
        transitions: wizardData.transitions!,
        principles: wizardData.principles!,
        trainingLogistics: wizardData.trainingLogistics!,
      };
      await aiIntelligenceService.submitOnboarding(teamId, context);
      onComplete();
    } catch (err: any) {
      setError(
        err.response?.data?.error?.message || 'Failed to submit coaching DNA. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStepClick = (stepId: number) => {
    // Allow navigating to completed steps or the next available step
    if (completedSteps.includes(stepId) || stepId <= Math.max(...completedSteps, 0) + 1) {
      setCurrentStep(stepId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-gray-500">Loading your progress...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6 max-w-6xl mx-auto">
      {/* Left sidebar - Progress indicator */}
      <div className="lg:w-64 flex-shrink-0">
        <div className="bg-white rounded-xl border border-gray-200 p-4 lg:sticky lg:top-4">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Progress
          </h3>
          <nav aria-label="Wizard progress">
            <ol className="space-y-1">
              {WIZARD_STEPS.map((step) => {
                const isCompleted = completedSteps.includes(step.id);
                const isCurrent = currentStep === step.id;
                const isUpcoming = !isCompleted && !isCurrent;

                return (
                  <li key={step.id}>
                    <button
                      onClick={() => handleStepClick(step.id)}
                      disabled={isUpcoming && step.id > Math.max(...completedSteps, 0) + 1}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition-all min-h-[44px] ${
                        isCurrent
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : isCompleted
                          ? 'text-green-700 hover:bg-green-50 cursor-pointer'
                          : 'text-gray-400 cursor-not-allowed'
                      }`}
                      aria-current={isCurrent ? 'step' : undefined}
                    >
                      <span className="flex-shrink-0">
                        {isCompleted ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : isCurrent ? (
                          <div className="w-5 h-5 rounded-full border-2 border-blue-500 flex items-center justify-center">
                            <div className="w-2 h-2 rounded-full bg-blue-500" />
                          </div>
                        ) : (
                          <Circle className="w-5 h-5 text-gray-300" />
                        )}
                      </span>
                      <span className="truncate">{step.shortLabel}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </nav>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 min-w-0">
        <div className="bg-white rounded-xl border border-gray-200 p-6 lg:p-8">
          {/* Step content */}
          <div className="min-h-[400px]">
            {currentStep === 0 && <WelcomeStep />}
            {currentStep === 1 && (
              <IdentityStep
                data={wizardData.identity!}
                onChange={(data) => updateStepData('identity', data)}
              />
            )}
            {currentStep === 2 && (
              <FormationStep
                data={wizardData.formation!}
                onChange={(data) => updateStepData('formation', data)}
              />
            )}
            {currentStep === 3 && (
              <InPossessionStep
                data={wizardData.inPossession!}
                onChange={(data) => updateStepData('inPossession', data)}
              />
            )}
            {currentStep === 4 && (
              <OutOfPossessionStep
                data={wizardData.outOfPossession!}
                onChange={(data) => updateStepData('outOfPossession', data)}
              />
            )}
            {currentStep === 5 && (
              <TransitionsStep
                data={wizardData.transitions!}
                onChange={(data) => updateStepData('transitions', data)}
              />
            )}
            {currentStep === 6 && (
              <PrinciplesStep
                principlesData={wizardData.principles!}
                logisticsData={wizardData.trainingLogistics!}
                onPrinciplesChange={(data) => updateStepData('principles', data)}
                onLogisticsChange={(data) => updateStepData('trainingLogistics', data)}
              />
            )}
            {currentStep === 7 && (
              <SummaryStep data={wizardData as OnboardingContext} />
            )}
          </div>

          {/* Error message */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={handleBack}
              disabled={currentStep === 0}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
                currentStep === 0
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            {currentStep < WIZARD_STEPS.length - 1 ? (
              <button
                onClick={handleNext}
                disabled={!validateCurrentStep()}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors min-h-[44px] ${
                  validateCurrentStep()
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Confirm & Generate Plan
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
